import * as React from "react";
import { graphql, PageProps } from "gatsby";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

import { DEFAULT_LANGUAGE } from "@config/consts";

import { AVAILABLE_COUNTRIES, ORIGIN } from "@typings/globals";

import { langFromTag } from "@services/LangFromTag/LangFromTag";

import Layout from "../components/Layout";
import SEO, { SeoProps } from "../components/SEO";
import {
  ApplyPage as ApplyPageShared,
  PageApplyPrismicData,
} from "../shared/ApplyPage";

interface PageApplyPrismicDataWithSeo extends PageApplyPrismicData, SeoProps {
  origin: ORIGIN;
  is_teaser_version: boolean;
}

type Props = PageProps<
  {
    prismicApplyPage: {
      data: PageApplyPrismicDataWithSeo;
    };
  },
  {
    lang: string;
    country: AVAILABLE_COUNTRIES;
    origin: ORIGIN;
  }
>;

const ApplyPage = (props: Props): React.ReactElement => {
  const { prismicApplyPage } = props.data;
  const origin = prismicApplyPage.data.origin;

  const lang = props.pageContext.lang;
  const country = props.pageContext.country;

  const seoData: SeoProps = {
    seo_title: prismicApplyPage.data.seo_title,
    seo_description: prismicApplyPage.data.seo_description,
    seo_image: prismicApplyPage.data.seo_image,
  };

  return (
    <Layout
      isFullWidth
      language={langFromTag(lang) || DEFAULT_LANGUAGE}
      country={country}
      origin={origin}
      isHomeCleaningCTAEnabled={false}
    >
      <SEO
        title={seoData.seo_title?.text || ""}
        description={seoData.seo_description?.text || ""}
        path={props.location.pathname}
        {...seoData}
      />
      <ApplyPageShared
        prismicData={props.data.prismicApplyPage}
        showStaffImage={props.pageContext.origin !== ORIGIN.BATWORK}
      />
    </Layout>
  );
};

export default withPrismicPreview(ApplyPage);

export const applyPageQuery = graphql`
  query ApplyPageQuery($id: String) {
    prismicApplyPage(id: { eq: $id }) {
      _previewable
      data {
        is_teaser_version
        country
        origin

        # ==== SEO ====
        seo_title {
          text
        }
        seo_description {
          text
        }
        seo_image {
          gatsbyImageData(width: 1200)
        }

        # ==== HERO SECTION ====
        hero_title {
          text
        }
        hero_description {
          text
        }
        hero_action_button {
          text
        }
        hero_action_button_link {
          text
        }
        hero_image {
          alt

          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: { q: 95 }
            width: 1980
          )
        }
        hero_image_mobile {
          alt
          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: { q: 95 }
            width: 1980
          )
        }

        # ==== HOW IT WORKS SECTION ====
        how_it_works_title {
          text
        }
        how_it_works_step_one_description {
          text
        }
        how_it_works_step_one_icon

        how_it_works_step_two_description {
          text
        }
        how_it_works_step_two_icon

        how_it_works_step_three_description {
          text
        }
        how_it_works_step_three_icon

        # ==== ADVANTAGES SECTION ====
        advantages_title {
          text
        }
        advantages_list {
          advantages_list_item {
            text
          }
        }
        advantages_action_button {
          text
        }
        advantages_action_button_link {
          text
        }

        # ==== STAFF SECTION ====
        staff_image {
          alt
          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: { q: 95 }
            width: 1980
          )
        }

        # ==== TESTIMONIALS SECTION ====
        testimonials_title {
          text
        }
        testimonials_list {
          testimonial_quote {
            text
          }
          testimonial_author {
            text
          }
          testimonial_rating
        }
      }
    }
  }
`;
